import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, CircleDollarSign, RefreshCw, Target, Trophy, TrendingUp } from 'lucide-react';
import { apiService } from '../services/apiService';
import { TenderPerformance as TenderPerformanceData, TenderPerformanceDimension } from '../types';

const formatBRL = (value: number) => new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL', maximumFractionDigits: 0,
}).format(Number(value || 0));

const DimensionTable: React.FC<{ title: string; items: TenderPerformanceDimension[] }> = ({ title, items }) => (
  <section className="border border-line dark:border-line-dark rounded-lg overflow-hidden bg-surface dark:bg-surface-dark">
    <header className="px-4 py-3 border-b border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt">
      <h2 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">{title}</h2>
    </header>
    {items.length ? <div className="divide-y divide-line dark:divide-line-dark">
      {items.map((item) => <div key={item.label} className="px-4 py-2.5 grid grid-cols-[1fr_auto] gap-3 items-center text-sm">
        <div className="min-w-0"><p className="truncate font-medium text-ink-text dark:text-ink-text-dark">{item.label}</p><p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{formatBRL(item.value)}</p></div>
        <span className="tabular-nums font-semibold text-ink-title dark:text-ink-title-dark">{item.count}</span>
      </div>)}
    </div> : <p className="px-4 py-8 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Sem dados no período.</p>}
  </section>
);

const TenderPerformance: React.FC = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState<TenderPerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setData(await apiService.getTenderPerformance(days));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Não foi possível carregar os indicadores.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  const maxFunnel = useMemo(() => Math.max(...(data?.funnel.map((item) => item.count) || [1]), 1), [data]);
  const summary = data?.summary;

  return <div className="px-4 sm:px-6 py-5 max-w-[1480px] mx-auto space-y-5">
    <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 className="text-xl font-semibold text-ink-title dark:text-ink-title-dark">Indicadores de licitações</h1>
        <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Funil, conversão, fontes e resultados baseados nos registros gravados no sistema.</p>
      </div>
      <div className="flex items-center gap-2">
        <select value={days} onChange={(event) => setDays(Number(event.target.value))} className="h-9 rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-3 text-sm text-ink-text dark:text-ink-text-dark">
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
          <option value={365}>Último ano</option>
          <option value={3650}>Histórico completo</option>
        </select>
        <button type="button" onClick={() => void load()} className="w-9 h-9 inline-grid place-items-center rounded-md border border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark" title="Atualizar indicadores"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
      </div>
    </header>

    {error ? <div className="border border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30 rounded-lg p-4 text-sm text-rose-800 dark:text-rose-300">{error}</div> : null}

    <section className="grid grid-cols-2 xl:grid-cols-4 border border-line dark:border-line-dark rounded-lg overflow-hidden divide-x divide-y xl:divide-y-0 divide-line dark:divide-line-dark">
      {[
        { label: 'Em carteira', value: summary?.activePortfolio || 0, icon: Target, tone: 'text-sky-600' },
        { label: 'Taxa de qualificação', value: `${summary?.conversionRate || 0}%`, icon: TrendingUp, tone: 'text-violet-600' },
        { label: 'Taxa de êxito', value: `${summary?.winRate || 0}%`, icon: Trophy, tone: 'text-emerald-600' },
        { label: 'Valor ganho', value: formatBRL(summary?.wonValue || 0), icon: CircleDollarSign, tone: 'text-brand-600' },
      ].map(({ label, value, icon: Icon, tone }) => <div key={label} className="p-4 bg-surface dark:bg-surface-dark">
        <Icon className={`w-4 h-4 ${tone}`} /><p className="mt-3 text-2xl font-semibold tabular-nums text-ink-title dark:text-ink-title-dark">{loading ? '...' : value}</p><p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{label}</p>
      </div>)}
    </section>

    <section className="border border-line dark:border-line-dark rounded-lg bg-surface dark:bg-surface-dark p-4">
      <div className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-brand-600" /><h2 className="text-base font-semibold text-ink-title dark:text-ink-title-dark">Funil operacional</h2></div>
      <div className="mt-5 space-y-3">
        {(data?.funnel || []).map((item) => <div key={item.key} className="grid grid-cols-[minmax(120px,200px)_1fr_auto] gap-3 items-center text-sm">
          <span className="text-ink-text dark:text-ink-text-dark">{item.label}</span>
          <div className="h-2 rounded-full bg-surface-alt dark:bg-surface-dark-alt overflow-hidden"><div className="h-full bg-brand-500 rounded-full" style={{ width: `${Math.max((item.count / maxFunnel) * 100, item.count ? 3 : 0)}%` }} /></div>
          <span className="tabular-nums font-semibold text-ink-title dark:text-ink-title-dark text-right">{item.count}</span>
        </div>)}
      </div>
      <p className="mt-4 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Valor sob acompanhamento: {formatBRL(summary?.trackedValue || 0)}. Processos julgados: {summary?.judged || 0}; ganhos: {summary?.won || 0}; não ganhos: {summary?.lost || 0}.</p>
    </section>

    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <DimensionTable title="Fontes que geraram carteira" items={data?.breakdowns.sources || []} />
      <DimensionTable title="Categorias mais presentes" items={data?.breakdowns.categories || []} />
      <DimensionTable title="Distribuição por UF" items={data?.breakdowns.states || []} />
      <DimensionTable title="Modalidades" items={data?.breakdowns.modalities || []} />
    </div>

    <DimensionTable title="Motivos de não êxito registrados" items={data?.lossReasons || []} />
  </div>;
};

export default TenderPerformance;
